import { BoardAdTypeOption, BoardAdTypeOptions } from '@/_shared/types'
import { BoardDialog } from '@/components/BoardDialog.tsx'
import { DeleteBoardDialog } from '@/components/DeleteBoard.tsx'
import { MasonryComponent, TypedResponsiveMasonry } from '@/components/ResponsiveMasonryWrapper.tsx'
import { Stack, Text } from '@/components/custom-components'
import { FeatureTabs } from '@/components/custom-components/FeatureTabs'
import { Loader } from '@/components/custom-components/Loader'
import { ErrorDisplay } from '@/components/error.tsx'
import { Button } from '@/components/shadcn/button'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { AdCard, DiscoverAdCard } from '@/components/templates/AdCard'
import { Ad } from '@/components/templates/DiscoverGridView'
import useCardLayoutWithImpressions from '@/hooks/useCardLayoutWithImpressions.tsx'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import showToastNotification from '@/hooks/useShowToast'
import { trpc } from '@/utils/trpc.ts'
import { Link as LinkIcon, Pencil, Trash } from '@mynaui/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
const LIMIT = 10

export const Route = createFileRoute('/feeds/boards/$boardID/ads')({
  component: BoardAds,
})

function BoardAds() {
  const { boardID } = Route.useParams()
  const navigate = useNavigate()

  const [editBoardDialogOpen, setEditBoardDialogOpen] = useState<boolean>(false)

  const [deleteBoardDialogOpen, setDeleteBoardDialogOpen] = useState<boolean>(false)

  const [, copyBoardAdToClipboard] = useCopyToClipboard()

  const {
    data: boardDetails,
    isLoading,
    error,
  } = trpc.getUserBoardById.useQuery(
    {
      boardId: boardID,
    },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )
  const {
    data: boardAds,
    isLoading: adsLoading,
    isError: adsError,
    isFetchingNextPage: isFetchingNextAdsPage,
    fetchNextPage: fetchNextAdsPage,
    hasNextPage: hasNextAdsPage,
    isRefetching: adsRefetching,
  } = trpc.getBoardAdInspirationPaginated.useInfiniteQuery(
    { limit: LIMIT, boardId: boardID },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  const { squareRef, desiredCardWidth, columns, gutterWidth } = useCardLayoutWithImpressions()

  const { ref: scrollRef, inView } = useInView({
    threshold: 0,
    trackVisibility: true,
    delay: 100,
  })

  // Fetch next page when reaching the bottom
  useEffect(() => {
    if (inView && hasNextAdsPage && !isFetchingNextAdsPage) {
      if (hasNextAdsPage && !isFetchingNextAdsPage && (boardAds?.pages?.at(-1)?.results?.length ?? 0) >= LIMIT) {
        fetchNextAdsPage()
      }
    }
  }, [inView, hasNextAdsPage, isFetchingNextAdsPage, fetchNextAdsPage])

  const allSavedAds = boardAds?.pages.flatMap((page) => page.results) || []

  const noAdsAvailable = allSavedAds.filter((data) => !data.adTemplate).length === 0

  const totalAdsCount = boardAds && boardAds.pages.length > 0 ? boardAds.pages[0].results.length : 0

  // HERE ARE ALL THE TEMPLATE STUFF DELETE WHEN DONE
  const {
    data: boardAdTemplates,
    isLoading: adsLoadingTemp,
    isError: adsErrorTemp,
    isFetchingNextPage: isFetchingNextAdsPageTemp,
    fetchNextPage: fetchNextAdsPageTemp,
    hasNextPage: hasNextAdsPageTemp,
    isRefetching: adsRefetchingTemp,
  } = trpc.getBoardAdTemplatesPaginated.useInfiniteQuery(
    { limit: LIMIT, boardId: boardID },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  // Fetch next page when reaching the bottom
  useEffect(() => {
    if (inView && hasNextAdsPageTemp && !isFetchingNextAdsPageTemp) {
      if (
        hasNextAdsPageTemp &&
        !isFetchingNextAdsPageTemp &&
        (boardAdTemplates?.pages?.at(-1)?.results?.length ?? 0) >= LIMIT
      ) {
        fetchNextAdsPageTemp()
      }
    }
  }, [inView, hasNextAdsPageTemp, isFetchingNextAdsPageTemp, fetchNextAdsPageTemp])

  const allSavedAdsTemp = boardAdTemplates?.pages.flatMap((page) => page.results) || []

  const noAdsAvailableTemp = allSavedAdsTemp.length === 0

  const totalAdsCountTemp =
    boardAdTemplates && boardAdTemplates.pages.length > 0 ? boardAdTemplates.pages[0].results.length : 0
  // HERE ARE ALL THE TEMPLATE STUFF DELETE WHEN DONE

  if (adsError || adsErrorTemp) {
    return (
      <div className={'px-0'}>
        <ErrorDisplay />
      </div>
    )
  }

  if (!boardAds || adsLoading || adsRefetching || !boardAdTemplates || adsLoadingTemp || adsRefetchingTemp) {
    return (
      <div className="flex justify-center items-center w-full h-screen m-auto">
        <Loader />
      </div>
    )
  }

  return (
    <>
      {editBoardDialogOpen && boardDetails && (
        <BoardDialog
          data={{
            name: boardDetails.name,
            id: boardDetails.id,
            description: boardDetails.description || '',
          }}
          open={editBoardDialogOpen}
          onOpenChange={() => setEditBoardDialogOpen(false)}
        />
      )}
      {deleteBoardDialogOpen && boardDetails && (
        <DeleteBoardDialog
          data={{
            name: boardDetails.name,
            id: boardDetails.id,
            description: boardDetails.description || '',
          }}
          open={deleteBoardDialogOpen}
          onOpenChange={() => setDeleteBoardDialogOpen(false)}
        />
      )}
      <Stack className="gap-3 lg:gap-8">
        <Stack className="gap-3 lg:gap-8">
          <Stack className="gap-3 lg:gap-2">
            {boardDetails ? (
              <div className={'flex justify-between gap-5 items-start'}>
                <Stack className="gap-2">
                  <Text weight="semibold" size={'xxl'}>
                    {boardDetails?.name}
                  </Text>
                  <div className={`flex gap-1 items-center`}>
                    <Text className="text-thememutedforeground w-fit" size={'base'} weight={'normal'}>
                      {boardDetails?.description}
                    </Text>
                  </div>
                </Stack>
                <Stack className="gap-3 items-end">
                  <div className="flex items-center justify-end gap-6">
                    <FeatureTabs
                      tabItems={[
                        {
                          name: 'Ads',
                          link: `/feeds/boards/${boardID}/ads`,
                        },
                        {
                          name: 'Landers',
                          link: `/feeds/boards/${boardID}/ad-landers`,
                        },
                        {
                          name: 'Emails',
                          link: `/feeds/boards/${boardID}/emails`,
                        },
                      ]}
                    />
                    <Select
                      value={BoardAdTypeOptions.ALL_SAVED}
                      onValueChange={(value: BoardAdTypeOption) => {
                        if (value === BoardAdTypeOptions.ONLY_TEMPLATES) {
                          navigate({
                            to: `/feeds/boards/$boardID/ad-templates`,
                            params: { boardID },
                          })
                        }
                      }}
                    >
                      <SelectTrigger className="w-33 h-8 max-h-8">
                        <SelectValue placeholder="Ad Type" className="rounded-md" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {Object.values(BoardAdTypeOptions).map((item) => (
                            <SelectItem key={item} value={item} className="py-1.5">
                              {item}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-1.5 h-8">
                    <Button
                      onClick={() => {
                        copyBoardAdToClipboard(`${window.location.origin}/feeds/boards/${boardDetails?.id}/ads`)
                        showToastNotification('success', {
                          message: 'Copied!',
                        })
                      }}
                      className="hidden lg:flex gap-1.5"
                      variant={'outline'}
                      size={'sm'}
                    >
                      <LinkIcon className="w-5 h-5" />
                      Share
                    </Button>
                    <Button
                      onClick={() => {
                        copyBoardAdToClipboard(`${window.location.origin}/feeds/boards/${boardDetails?.id}/ads`)
                        showToastNotification('success', {
                          message: 'Copied!',
                        })
                      }}
                      className="lg:hidden flex gap-1.5"
                      variant={'outline'}
                      size={'iconMid'}
                    >
                      <LinkIcon className="w-5 h-5" />
                    </Button>
                    <Button
                      onClick={() => setEditBoardDialogOpen(true)}
                      className="gap-1.5"
                      variant={'outline'}
                      size={'iconMid'}
                    >
                      <Pencil className="w-5 h-5" />
                    </Button>
                    <Button
                      onClick={() => setDeleteBoardDialogOpen(true)}
                      className="gap-1.5"
                      variant={'outline'}
                      size={'iconMid'}
                    >
                      <Trash className="w-5 h-5" />
                    </Button>
                  </div>
                  <Text
                    className="text-thememutedforeground w-fit text-nowrap hidden lg:flex"
                    size={'base'}
                    weight={'normal'}
                  >
                    {totalAdsCount + totalAdsCountTemp} {totalAdsCount + totalAdsCountTemp == 1 ? ' Item' : ' Items'}
                  </Text>
                </Stack>
              </div>
            ) : null}
          </Stack>
        </Stack>

        {isLoading ? (
          <div className={'flex justify-center items-center w-full h-screen m-auto'}>
            <Loader />
          </div>
        ) : error ? (
          <ErrorDisplay />
        ) : (
          <div>
            {noAdsAvailable && noAdsAvailableTemp ? (
              <div className={'lg:h-96 flex justify-center items-center'}>
                <p className="text-center leading-5">No ad has been added to this collection.</p>
              </div>
            ) : (
              <div ref={squareRef} className={'relative w-full pb-10'}>
                <div>
                  <TypedResponsiveMasonry
                    columnsCountBreakPoints={columns ? { 0: columns } : {}} // Columns is determined by the width of the container
                  >
                    <MasonryComponent gutter={gutterWidth / 16 + 'rem'}>
                      {allSavedAds.map((ad) =>
                        !ad.adTemplate ? (
                          <DiscoverAdCard
                            key={ad.id}
                            DesiredWidth={desiredCardWidth}
                            adData={ad as Ad}
                            IsPublic={false}
                            IsLocked={false}
                          />
                        ) : ad.adTemplate ? (
                          <AdCard
                            key={ad.adTemplate?.atID}
                            showTemplateButton
                            adData={{
                              Ad: ad.adTemplate,
                              IsLocked: false,
                              IsPublic: false,
                              brandName: undefined,
                              shouldInvalidateCache: false,
                              DesiredWidth: desiredCardWidth,
                            }}
                          />
                        ) : (
                          <></>
                        ),
                      )}
                      {allSavedAdsTemp.map((ad) => {
                        return (
                          <AdCard
                            key={ad.atID}
                            showTemplateButton
                            adData={{
                              Ad: ad,
                              IsLocked: false,
                              IsPublic: false,
                              brandName: undefined,
                              shouldInvalidateCache: false,
                              DesiredWidth: desiredCardWidth,
                            }}
                          />
                        )
                      })}
                    </MasonryComponent>
                  </TypedResponsiveMasonry>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Bottom scroll loader */}
        {hasNextAdsPage && (
          <div ref={scrollRef} className="flex justify-center w-full min-h-14 h-14">
            {isFetchingNextAdsPage && <Loader />}
          </div>
        )}
      </Stack>
    </>
  )
}
